import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs } from "@reduxjs/toolkit/query/react";
import { resolveApiBaseUrl } from "config/api.ts";
import type { ErrorData } from "app/types/error.ts";
import { removeCredentials } from "features/auth/authSlice.ts";
import { removePermissions } from "features/permissions/permissionsSlice.ts";

const createDynamicBaseQuery = () =>
  fetchBaseQuery({
    baseUrl: resolveApiBaseUrl(),
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem("token");
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  });

export const baseQueryWithAuth = (async (args, api, extraOptions) => {
  const requestToken = sessionStorage.getItem("token");
  const result = await createDynamicBaseQuery()(args, api, extraOptions);

  if (result.error?.status === 401) {
    const token = sessionStorage.getItem("token");
    // A response from a previous/anonymous session must not revoke a new login.
    if (!requestToken || requestToken !== token) {
      return result;
    }

    sessionStorage.removeItem("token");
    sessionStorage.removeItem("permissions");
    api.dispatch(removeCredentials());
    api.dispatch(removePermissions());
  }

  return result;
}) as BaseQueryFn<string | FetchArgs, unknown, ErrorData>;

export const apiSlice = createApi({
  baseQuery: baseQueryWithAuth,
  endpoints: () => ({}),
  tagTypes: [
    "dashboard",
    "basins",
    "bolCounties",
    "bolMarkets",
    "bols",
    "bolImages",
    "bolProducts",
    "carriers",
    "companies",
    "customerMailers",
    "counties",
    "customerContacts",
    "customers",
    "drivers",
    "jobTypes",
    "jobTypeGroups",
    "loginNotice",
    "loadOrigins",
    "mailerTemplates",
    "markets",
    "notificationTemplates",
    "opisnetRolfsonMap",
    "opisnetRolfsonMapSnapshot",
    "order",
    "orders",
    "ordersBulkUpload",
    "orderStatus",
    "products",
    "packageTypes",
    "releaseNotes",
    "refineries",
    "refineryAddresses",
    "resources",
    "rigs",
    "rigLocations",
    "rolePermissions",
    "roles",
    "salesReps",
    "settings",
    "shippingPapers",
    "systemAlerts",
    "taxes",
    "ticket",
    "ticketProducts",
    "ticketTaxes",
    "ticketTypes",
    "ticketPapers",
    "tickets",
    "timeSlots",
    "trucks",
    "units",
    "users",
    "vendors",
    "invoiceUpdateJob",
    "invoiceUpdateTickets",
    "cronJobs",
    "sqlMigrations",
    "typesense",
    "environment",
    "pageVideoPopups",
  ],
});
